import * as z from "zod";

const StickyCTASurfaceStyleSchema = z.object({
  backgroundColor: z.string().optional(),
  color: z.string().optional(),
  borderColor: z.string().optional(),
});
export type StickyCTASurfaceStyle = z.infer<typeof StickyCTASurfaceStyleSchema>;

const StickyCTABaseSchema = z.object({
  ctaText: z.string().optional(),
  displayProductId: z.string().optional(),
  displayVariantId: z.string().optional(),
  displaySellingPlanId: z.string().optional(),
  displayQuantity: z.number().optional(),
  bannerEntryPixelsFromTop: z
    .number()
    .int({ message: "Pixels from top must be an integer" })
    .min(0, { message: "Pixels from top must be 0 or greater" })
    .optional(),
  content: z.string().optional(),
  /** When true with `content`, reverse CTA/content order (mobile: content above; desktop: CTA left). */
  swapContentLayout: z.boolean().optional(),
  style: StickyCTASurfaceStyleSchema.optional(),
  buttonStyle: StickyCTASurfaceStyleSchema.optional(),
  replaceProductFormStickyCTA: z.boolean().optional(),
});

export const StickyCtaActionType = {
  EXTERNAL_URL: "external_url",
  SCROLL_TO_SECTION: "scroll_to_section",
  ADD_TO_CART: "add_to_cart",
  GO_TO_CHECKOUT: "go_to_checkout",
  OPEN_PRODUCT_PAGE: "open_product_page",
} as const;

const StickyCTAExternalUrlActionConfigSchema = StickyCTABaseSchema.extend({
  actionType: z.literal(StickyCtaActionType.EXTERNAL_URL),
  externalUrl: z.url({ error: "External URL is required" }),
});
export type StickyCTAExternalUrlActionConfig = z.infer<typeof StickyCTAExternalUrlActionConfigSchema>;
export const isStickyCTAExternalUrlActionConfig = (
  config?: StickyCTAActionConfig,
): config is StickyCTAExternalUrlActionConfig => {
  return config?.actionType === StickyCtaActionType.EXTERNAL_URL;
};

const StickyCTAScrollToSectionActionConfigSchema = StickyCTABaseSchema.extend({
  actionType: z.literal(StickyCtaActionType.SCROLL_TO_SECTION),
  sectionSelector: z.string({ error: "Section selector is required" }),
});

export type StickyCTAScrollToSectionActionConfig = z.infer<typeof StickyCTAScrollToSectionActionConfigSchema>;
export const isStickyCTAScrollToSectionActionConfig = (
  config?: StickyCTAActionConfig,
): config is StickyCTAScrollToSectionActionConfig => {
  return config?.actionType === StickyCtaActionType.SCROLL_TO_SECTION;
};

const StickyCTAProductActionBaseSchema = StickyCTABaseSchema.extend({
  displayProductId: z.string({ error: "Product is required" }),
});

const StickyCTAAddToCartActionConfigSchema = StickyCTAProductActionBaseSchema.extend({
  actionType: z.literal(StickyCtaActionType.ADD_TO_CART),
});
export type StickyCTAAddToCartActionConfig = z.infer<typeof StickyCTAAddToCartActionConfigSchema>;
export const isStickyCTAAddToCartActionConfig = (
  config?: StickyCTAActionConfig,
): config is StickyCTAAddToCartActionConfig => config?.actionType === StickyCtaActionType.ADD_TO_CART;

const StickyCTAGoToCheckoutActionConfigSchema = StickyCTAProductActionBaseSchema.extend({
  actionType: z.literal(StickyCtaActionType.GO_TO_CHECKOUT),
});
export type StickyCTAGoToCheckoutActionConfig = z.infer<typeof StickyCTAGoToCheckoutActionConfigSchema>;
export const isStickyCTAGoToCheckoutActionConfig = (
  config?: StickyCTAActionConfig,
): config is StickyCTAGoToCheckoutActionConfig => config?.actionType === StickyCtaActionType.GO_TO_CHECKOUT;

const StickyCTAOpenProductPageActionConfigSchema = StickyCTAProductActionBaseSchema.extend({
  actionType: z.literal(StickyCtaActionType.OPEN_PRODUCT_PAGE),
});
export type StickyCTAOpenProductPageActionConfig = z.infer<typeof StickyCTAOpenProductPageActionConfigSchema>;
export const isStickyCTAOpenProductPageActionConfig = (
  config?: StickyCTAActionConfig,
): config is StickyCTAOpenProductPageActionConfig => config?.actionType === StickyCtaActionType.OPEN_PRODUCT_PAGE;

export const StickyCTAActionConfigSchema = z.discriminatedUnion("actionType", [
  StickyCTAExternalUrlActionConfigSchema,
  StickyCTAScrollToSectionActionConfigSchema,
  StickyCTAAddToCartActionConfigSchema,
  StickyCTAGoToCheckoutActionConfigSchema,
  StickyCTAOpenProductPageActionConfigSchema,
]);
export type StickyCTAActionConfig = z.infer<typeof StickyCTAActionConfigSchema>;

/** Variants where product display is optional; on save `displayProductId` is normalized to a string */
export const STICKY_CTA_ACTION_TYPES_WITH_OPTIONAL_DISPLAY_PRODUCT = [
  StickyCtaActionType.EXTERNAL_URL,
  StickyCtaActionType.SCROLL_TO_SECTION,
] as const satisfies ReadonlyArray<StickyCTAActionConfig["actionType"]>;

export function isStickyCtaActionWithOptionalDisplayProduct(actionType: StickyCTAActionConfig["actionType"]): boolean {
  return (
    STICKY_CTA_ACTION_TYPES_WITH_OPTIONAL_DISPLAY_PRODUCT as readonly StickyCTAActionConfig["actionType"][]
  ).includes(actionType);
}
